/** @jsxImportSource @emotion/react */
import type { ReactNode } from 'react';
import { useEffect } from 'react';
import { Button, type ButtonTone } from '../../atoms/Button/Button';
import {
  backdropStyle,
  panelStyle,
  titleStyle,
  messageStyle,
  actionsStyle,
} from './ConfirmDialog.styles';

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  /** 본문 설명. 문자열 또는 노드. */
  message?: ReactNode;
  /** 확인 버튼 문구. 기본 '확인'. */
  confirmLabel?: string;
  /** 취소 버튼 문구. 기본 '취소'. */
  cancelLabel?: string;
  /** 확인 버튼 색 계열. 기본 'primary'. */
  confirmTone?: ButtonTone;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * 중앙 확인 모달. 파괴적/비가역 액션 실행 전 사용자 확인에 활용.
 * 백드롭 클릭·Esc = 취소. role=dialog로 접근성 확보.
 */
export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = '확인',
  cancelLabel = '취소',
  confirmTone = 'primary',
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onCancel]);

  if (!open) return null;

  return (
    <div css={backdropStyle} onClick={onCancel}>
      <div
        css={panelStyle}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 css={titleStyle}>{title}</h2>
        {message !== undefined && <p css={messageStyle}>{message}</p>}
        <div css={actionsStyle}>
          <Button variant="outline" tone="neutral" onClick={onCancel}>
            {cancelLabel}
          </Button>
          <Button tone={confirmTone} onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
